import React from 'react';
import type { OrgNavigationFilters } from './OrgNavigationContent';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import {
    createDefaultOrgNavigationFilters,
    getStoredOrgNavigationFilters,
    hasStoredOrgNavigationFilters,
    setStoredOrgNavigationFilters,
} from './orgNavigationFiltersStorage';
import {
    areSerializableFiltersEqual,
    buildOrgNavigationSearchParams,
    hasOrgNavigationFiltersInSearch,
    ORG_OVERVIEW_PATH,
    parseOrgNavigationFiltersFromSearch,
} from './orgNavigationUrlParams';

interface OrgNavigationFiltersContextValue {
    filters: OrgNavigationFilters;
    hasStoredFilters: boolean;
    setFilters: React.Dispatch<React.SetStateAction<OrgNavigationFilters>>;
}

const OrgNavigationFiltersContext = React.createContext<OrgNavigationFiltersContextValue | null>(null);

export const OrgNavigationFiltersProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const history = useHistory();
    const location = useLocation();
    const isOrgOverviewRoute = !!matchPath(location.pathname, {
        path: ORG_OVERVIEW_PATH,
        exact: true,
        strict: false,
    });
    const shouldUseUrlFiltersOnInitialLoad = isOrgOverviewRoute && hasOrgNavigationFiltersInSearch(location.search);
    const initialUrlFilters = React.useMemo(
        () => (shouldUseUrlFiltersOnInitialLoad ? parseOrgNavigationFiltersFromSearch(location.search) : null),
        [location.search, shouldUseUrlFiltersOnInitialLoad],
    );
    const [hasStoredFilters] = React.useState(() => hasStoredOrgNavigationFilters());
    const [filters, setFilters] = React.useState<OrgNavigationFilters>(() => {
        if (shouldUseUrlFiltersOnInitialLoad) {
            return parseOrgNavigationFiltersFromSearch(location.search);
        }

        return getStoredOrgNavigationFilters() ?? createDefaultOrgNavigationFilters();
    });
    const initialUrlFiltersRef = React.useRef(initialUrlFilters);

    React.useEffect(() => {
        if (initialUrlFiltersRef.current) {
            if (areSerializableFiltersEqual(filters, initialUrlFiltersRef.current)) {
                return;
            }

            initialUrlFiltersRef.current = null;
        }

        setStoredOrgNavigationFilters(filters);
    }, [filters]);

    React.useEffect(() => {
        if (!isOrgOverviewRoute) {
            return;
        }

        const nextSearchParams = buildOrgNavigationSearchParams(filters, location.search);
        const nextSearch = nextSearchParams.toString();
        const currentSearch = location.search.replace(/^\?/, '');

        if (nextSearch === currentSearch) {
            return;
        }

        history.replace({
            ...location,
            search: nextSearch,
        });
    }, [filters, history, isOrgOverviewRoute, location]);

    return (
        <OrgNavigationFiltersContext.Provider
            value={{
                filters,
                hasStoredFilters,
                setFilters,
            }}
        >
            {children}
        </OrgNavigationFiltersContext.Provider>
    );
};

export const useOrgNavigationFilters = (): OrgNavigationFiltersContextValue => {
    const context = React.useContext(OrgNavigationFiltersContext);

    if (!context) {
        throw new Error('useOrgNavigationFilters must be used within OrgNavigationFiltersProvider');
    }

    return context;
};
